import { userIsAdmin, userIsPractitioner, userIsStaff, userRpps } from '../../Controller/userRoles';

// Bit 25 is the subscription feature that enables the TLS workflow.
export const TLS_FEATURE_BIT = 25;

export function hasTlsFeature(subscriptionFeatures) {
    return Number.isFinite(Number(subscriptionFeatures))
        && (Number(subscriptionFeatures) & (1 << TLS_FEATURE_BIT)) !== 0;
}

// SPO2_alert is the only medical alert type — all others are non-medical.
export const NON_MEDICAL_ALERT_TYPES = new Set([
    'fever_alert', 'PR_alert',
    'bloodpressure_PR_alert', 'bloodpressure_sys_high_alert', 'bloodpressure_dia_alert',
    'weight_alert', 'eq5d_alert', 'eq5d_scale_alert', 'sf36_alert', 'mdasi_alert', 'pain_alert',
    'breathing_rate_alert', 'breathing_oxygen_rate_alert', 'breathing_leakage_alert', 'breathing_observance_alert',
    'reminder_alert', 'detach_alert', 'battery_alert', 'pos_alert',
]);

/**
 * Returns a function `(alertType) => boolean` that returns true when the ack
 * button should be DISABLED for that alert type, or null when there is no
 * restriction.
 *
 * Rules:
 *  - PSAD user with no psad_delegate on the patient → all alerts disabled.
 *  - Non-PSAD user when non-medical PSAD delegation is active → only non-medical
 *    alerts are disabled (i.e. the practitioner can still ack medical/SPO2 alerts).
 *  - Otherwise → no restriction (null).
 *
 * @param {boolean} isPsadUser
 * @param {string|null} psad_delegate   - patient field
 * @param {string|null} delegate_non_medical_alert - patient field ('no' = off)
 */
export function computeAlertAckDisabler(isPsadUser, psad_delegate, delegate_non_medical_alert) {
    if (isPsadUser && !psad_delegate) {
        return () => true;
    }

    const isNonMedicalExplicitlyOff = delegate_non_medical_alert === 'no';
    const hasNonMedicalPsadDelegate = !isNonMedicalExplicitlyOff && !!psad_delegate;

    if (hasNonMedicalPsadDelegate && !isPsadUser) {
        return (alertType) => NON_MEDICAL_ALERT_TYPES.has(alertType);
    }

    return null;
}

/**
 * Returns true when the user is allowed to perform TLS (télésurveillance)
 * validation for the given patient.
 *
 * Rules:
 *  - User must be a practitioner (has RPPS, profession, or category).
 *  - User must have a resolvable RPPS number.
 *  - If the patient has an active medical delegate RPPS, the user's RPPS must
 *    match it.
 *  - Otherwise, the user's RPPS must match the patient's speciality practitioner
 *    RPPS.
 *
 * @param {object} accountInformation
 * @param {{ delegate_medical_alert: string|null, rpps_id_practioner_speciality: string|null }} patientData
 */
export function computeCanValidateTls(accountInformation, patientData) {
    if (!userIsPractitioner(accountInformation)) return false;

    const currentUserRpps = userRpps(accountInformation);
    if (!currentUserRpps) return false;

    const delegateRpps = patientData.delegate_medical_alert;
    const hasMedicalDelegate = delegateRpps && delegateRpps !== 'no' && delegateRpps !== 'na';

    if (hasMedicalDelegate) {
        return currentUserRpps === String(delegateRpps);
    }

    return patientData.rpps_id_practioner_speciality
        ? currentUserRpps === String(patientData.rpps_id_practioner_speciality)
        : false;
}

/**
 * Returns true when the user may trigger TLS validation for the current page.
 * The TLS feature and role visibility rules must be satisfied, and at least
 * one patient on the page must have pneumology monitoring (lpp_code).
 *
 * @param {object} accountInformation
 * @param {number} subscriptionFeatures
 * @param {Array<object>} patients
 */
export function canValidateAllTls(accountInformation, subscriptionFeatures, patients) {
    return shouldShowTls(accountInformation, subscriptionFeatures)
        && Array.isArray(patients)
        && patients.some(patient => Boolean(patient?.lpp_code));
}

/**
 * Returns true when the TLS section should be visible at all for the user.
 * The current user's structure must have the TLS subscription feature, and
 * the user must be an admin (including a PSAD admin) or a practitioner.
 *
 * @param {object} accountInformation
 * @param {number} subscriptionFeatures - current user's structure bitmask
 */
export function shouldShowTls(accountInformation, subscriptionFeatures) {
    if (!hasTlsFeature(subscriptionFeatures)) return false;

    const profession = accountInformation?.profession;
    const isPractitioner =
        userIsStaff(accountInformation) &&
        profession !== 'psad' &&
        userIsPractitioner(accountInformation);
    return userIsAdmin(accountInformation) || isPractitioner;
}
